import React from 'react';
import styled from 'styled-components';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'text';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | React.MouseEventHandler<HTMLAnchorElement>;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  as?: React.ElementType;
  href?: string;
  to?: string;
  style?: React.CSSProperties;
}

const StyledButton = styled.button<{
  variant: ButtonVariant;
  size: ButtonSize;
  fullWidth: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.radii.md};
  transition: ${({ theme }) => theme.transitions.default};
  cursor: pointer;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  font-family: ${({ theme }) => theme.fonts.body};
  letter-spacing: -0.01em;
  position: relative;
  overflow: hidden;
  box-shadow: ${({ variant, theme }) => 
    variant === 'primary' || variant === 'secondary' 
      ? theme.shadows.sm 
      : 'none'};

  /* Size styles */
  ${({ size }) =>
    size === 'small' &&
    `
    font-size: 0.875rem;
    padding: 0.5rem 1.25rem;
    height: 2.25rem;
  `}

  ${({ size }) =>
    size === 'medium' &&
    `
    font-size: 0.9375rem;
    padding: 0.625rem 1.5rem;
    height: 2.75rem;
  `}

  ${({ size }) =>
    size === 'large' &&
    `
    font-size: 1rem;
    padding: 0.75rem 2rem;
    height: 3.25rem;
  `}

  /* Variant styles */
  ${({ variant, theme }) =>
    variant === 'primary' &&
    `
    background: ${theme.colors.gradient.cta};
    color: white;
    border: none;
    font-weight: 600;
    
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: ${theme.shadows.glow}, ${theme.shadows.md};
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: ${theme.shadows.sm};
    }
  `}

  ${({ variant, theme }) =>
    variant === 'secondary' &&
    `
    background: ${theme.colors.gradient.primary};
    color: white;
    border: none;
    
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: ${theme.shadows.md};
      opacity: 0.95;
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: ${theme.shadows.sm};
    }
  `}

  ${({ variant, theme }) =>
    variant === 'outline' &&
    `
    background-color: transparent;
    color: ${theme.colors.primary};
    border: 1px solid ${theme.colors.grayLight};
    
    &:hover:not(:disabled) {
      border-color: ${theme.colors.primary};
      color: ${theme.colors.primary};
      background-color: rgba(124, 58, 237, 0.04);
    }
    
    &:active:not(:disabled) {
      background-color: rgba(124, 58, 237, 0.08);
    }
  `}

  ${({ variant, theme }) =>
    variant === 'text' &&
    `
    background-color: transparent;
    color: ${theme.colors.primary};
    border: none;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    box-shadow: none;
    
    &:hover:not(:disabled) {
      color: ${theme.colors.primaryDark};
      background-color: rgba(124, 58, 237, 0.04);
    }
    
    &:active:not(:disabled) {
      background-color: rgba(124, 58, 237, 0.08);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  children,
  onClick,
  disabled = false,
  type = 'button',
  className,
  as,
  href,
  to,
  style,
}) => {
  return (
    <StyledButton
      as={as}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      onClick={onClick}
      disabled={disabled}
      type={type}
      className={className}
      href={href}
      to={to}
      style={style}
    >
      {children}
    </StyledButton>
  );
};

export default Button;
